'use client';
import React, { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const reviewerSteps = [
  {
    step: '01',
    title: 'Create Your Profile',
    desc: 'Sign up, complete KYC, and tell us your interests — from electronics to beauty products.',
    icon: 'UserCircleIcon',
    color: '#FF5722',
    bg: '#FFF3EE',
  },
  {
    step: '02',
    title: 'Pick Products to Review',
    desc: 'Browse products submitted by brands, pick what interests you, and get selected to review.',
    icon: 'ClipboardDocumentCheckIcon',
    color: '#F59E0B',
    bg: '#FFFBEB',
  },
  {
    step: '03',
    title: 'Review & Earn',
    desc: 'Receive the product, submit your honest review and feedback, and get paid via UPI within 7 days.',
    icon: 'BanknotesIcon',
    color: '#10B981',
    bg: '#ECFDF5',
  },
];

const brandSteps = [
  {
    step: '01',
    title: 'Submit Your Product',
    desc: 'List your product with review guidelines and the number of creators you need. Go live in under 10 minutes.',
    icon: 'RocketLaunchIcon',
    color: '#1A237E',
    bg: '#EEF0FB',
  },
  {
    step: '02',
    title: 'Creators Pick It',
    desc: 'Verified creators and reviewers browse your product and pick it based on their interests and expertise.',
    icon: 'UsersIcon',
    color: '#8B5CF6',
    bg: '#F5F3FF',
  },
  {
    step: '03',
    title: 'Get AI-Verified Reviews',
    desc: 'AI quality-checks every review for authenticity. Get GST invoices automatically.',
    icon: 'SparklesIcon',
    color: '#00BFA5',
    bg: '#E6FAF8',
  },
];

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll<HTMLElement>('.hiw-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.style.opacity = '1';
                card.style.transform = 'translateY(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="section-py bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="pill-badge mb-4">How It Works</span>
          <h2 className="font-heading font-bold text-foreground mb-4" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)' }}>
            Simple. Transparent. Rewarding.
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-base">
            Whether you want to earn money or get authentic reviews, we've made the process effortless.
          </p>
        </div>

        {/* Two-panel grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Reviewer Panel */}
          <div className="bg-white rounded-3xl p-8 border border-border shadow-sm">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 cta-gradient rounded-xl flex items-center justify-center shadow-sm">
                <Icon name="StarIcon" size={20} className="text-white" />
              </div>
              <div>
                <div className="font-heading font-bold text-base text-foreground">For Reviewers & Creators</div>
                <div className="text-xs text-muted-foreground">Earn ₹500–₹5,000 per month</div>
              </div>
            </div>
            <div className="space-y-6">
              {reviewerSteps.map((step, i) => (
                <div
                  key={step.step}
                  className="hiw-card flex gap-5"
                  style={{
                    opacity: 0,
                    transform: 'translateY(20px)',
                    transition: `opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1)`,
                    transitionDelay: `${i * 0.12}s`,
                  }}
                >
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: step.bg }}
                    >
                      <Icon name={step.icon as 'UserCircleIcon'} size={20} style={{ color: step.color }} />
                    </div>
                    {i < reviewerSteps.length - 1 && (
                      <div className="w-px flex-1 bg-border mt-1" style={{ minHeight: '24px' }} />
                    )}
                  </div>
                  <div className="pb-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: step.color }}>{step.step}</span>
                      <h3 className="font-heading font-semibold text-foreground text-sm">{step.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Brand Panel */}
          <div className="bg-white rounded-3xl p-8 border border-border shadow-sm">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 secondary-gradient rounded-xl flex items-center justify-center shadow-sm">
                <Icon name="BuildingStorefrontIcon" size={20} className="text-white" />
              </div>
              <div>
                <div className="font-heading font-bold text-base text-foreground">For Brands</div>
                <div className="text-xs text-muted-foreground">Get authentic reviews & real feedback</div>
              </div>
            </div>
            <div className="space-y-6">
              {brandSteps.map((step, i) => (
                <div
                  key={step.step}
                  className="hiw-card flex gap-5"
                  style={{
                    opacity: 0,
                    transform: 'translateY(20px)',
                    transition: `opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1)`,
                    transitionDelay: `${(i + 3) * 0.12}s`,
                  }}
                >
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: step.bg }}
                    >
                      <Icon name={step.icon as 'RocketLaunchIcon'} size={20} style={{ color: step.color }} />
                    </div>
                    {i < brandSteps.length - 1 && (
                      <div className="w-px flex-1 bg-border mt-1" style={{ minHeight: '24px' }} />
                    )}
                  </div>
                  <div className="pb-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: step.color }}>{step.step}</span>
                      <h3 className="font-heading font-semibold text-foreground text-sm">{step.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}